/** Shared configuration boundary for the production and development schemas. */
import type { SupabaseClientOptions } from '@supabase/supabase-js';
import { DEVELOPMENT_AUTH_BROKER_URL, SUPABASE_PUBLIC_IDS } from '@config/supabase';

export type DatabaseScope = 'public' | 'development';
const APPLICATION_REFS = new Set(['ewsahqwtupghisvbekvf', 'tciitqifqppvkthtjruc']);

export function databaseScope(): DatabaseScope {
  const scope = process.env.NEXT_PUBLIC_SUPABASE_SCHEMA?.trim() || 'public';
  if (scope !== 'public' && scope !== 'development') throw new Error('Unknown Supabase database scope');
  return scope;
}

export function identityMatchesDatabaseScope(user: { app_metadata?: Record<string, unknown> }): boolean {
  return (user.app_metadata?.bizrnr_environment === 'development') === (databaseScope() === 'development');
}

/** Configuration inspection only; Supabase verifies the signature on requests. */
export function credentialRole(key: string): string | undefined {
  try {
    const encoded = key.split('.')[1];
    if (!encoded) return undefined;
    const claims = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/'))) as { role?: unknown };
    return typeof claims.role === 'string' ? claims.role : undefined;
  } catch { return undefined; }
}

export function applicationProjectRef(url: string): string | undefined {
  try {
    const host = new URL(url).hostname;
    const ref = host.match(/^([a-z0-9]{20})\.supabase\.co$/)?.[1];
    return ref && APPLICATION_REFS.has(ref) ? ref : undefined;
  } catch { return undefined; }
}

function wrongEnvironment() {
  return Response.json({ msg: 'Identity belongs to another environment', code: 'identity_environment_mismatch' }, { status: 403 });
}

/** Production server handlers must reject development identities before using
 * their privileged database client. Database RLS alone cannot guard that seam. */
function productionClientOptions(url: string, options?: SupabaseClientOptions<string>): SupabaseClientOptions<string> {
  const originalFetch: typeof fetch = options?.global?.fetch ?? ((input, init) => fetch(input, init));
  return { ...options, global: { ...options?.global, fetch: async (input, init) => {
    const request = new Request(input, init);
    const destination = new URL(request.url);
    if (destination.origin !== new URL(url).origin) throw new Error('Supabase request escaped the configured project');
    if (destination.pathname.startsWith('/auth/v1/') &&
      credentialRole((request.headers.get('authorization') ?? '').replace(/^Bearer /, '')) === 'development_authenticated') return wrongEnvironment();
    const adminIdentity = destination.pathname.match(/^\/auth\/v1\/admin\/users\/([0-9a-f-]{36})(?:\/factors(?:\/[0-9a-f-]{36})?)?$/i);
    if (adminIdentity && ['PUT', 'DELETE'].includes(request.method)) {
      const identity = await originalFetch(`${url}/auth/v1/admin/users/${adminIdentity[1]}`, {
        headers: request.headers, signal: request.signal, redirect: 'error',
      });
      if (!identity.ok) return identity;
      const payload = await identity.json();
      if ((payload.user?.app_metadata ?? payload.app_metadata)?.bizrnr_environment === 'development') return wrongEnvironment();
    }
    if (destination.pathname === '/auth/v1/admin/generate_link' && request.method === 'POST') {
      const body = await request.clone().json();
      const identity = await originalFetch(`${url}/rest/v1/rpc/development_auth_identity_scope`, {
        method: 'POST', headers: { apikey: request.headers.get('apikey') ?? '',
          Authorization: request.headers.get('authorization') ?? '', 'Content-Type': 'application/json', 'Content-Profile': 'public' },
        body: JSON.stringify({ p_email: body.email }), signal: request.signal, redirect: 'error',
      });
      if (!identity.ok) return Response.json({ msg: 'Identity scope verification unavailable' }, { status: 503 });
      if ((await identity.json())?.development) return wrongEnvironment();
    }
    const response = await originalFetch(request);
    if (response.ok && (['/auth/v1/token', '/auth/v1/verify', '/auth/v1/user'].includes(destination.pathname) ||
      /^\/auth\/v1\/admin\/users\/[0-9a-f-]+$/i.test(destination.pathname))) {
      const payload = await response.clone().json();
      if ((payload.user?.app_metadata ?? payload.app_metadata)?.bizrnr_environment === 'development' ||
        (payload.access_token && credentialRole(payload.access_token) === 'development_authenticated')) return wrongEnvironment();
    }
    return response;
  } } };
}

export function scopedClientOptions(
  url: string,
  key: string,
  options?: SupabaseClientOptions<string>,
  scope: DatabaseScope = databaseScope(),
): SupabaseClientOptions<string> | undefined {
  // MAS and unrelated integrations retain their own database boundary.
  if (!applicationProjectRef(url)) return options;
  if (scope === 'public') return productionClientOptions(url, options);
  const role = credentialRole(key);
  if (role !== 'development_service' && role !== 'development_anon') {
    throw new Error('Development requires a restricted development credential; production/legacy keys are refused');
  }
  if (options?.db?.schema && options.db.schema !== 'development') {
    throw new Error('Development client cannot select another database schema');
  }
  const gatewayKey = process.env.NEXT_PUBLIC_SUPABASE_GATEWAY_KEY?.trim() ||
    (url === SUPABASE_PUBLIC_IDS.prodUrl ? SUPABASE_PUBLIC_IDS.prodAnonKey : '');
  if (!gatewayKey) throw new Error('Development Supabase gateway key is missing');
  const originalFetch: typeof fetch = options?.global?.fetch ?? ((input, init) => fetch(input, init));
  const scopedFetch: typeof fetch = async (input, init) => {
    const request = new Request(input, init);
    const destination = new URL(request.url);
    if (destination.origin !== new URL(url).origin) {
      throw new Error('Supabase request escaped the configured project');
    }
    if (role === 'development_service' &&
      (destination.pathname.startsWith('/auth/v1/admin/') || destination.pathname === '/auth/v1/logout')) {
      const authorization = request.headers.get('authorization');
      const userToken = authorization && authorization !== `Bearer ${key}`
        ? authorization.replace(/^Bearer /, '') : undefined;
      return originalFetch(DEVELOPMENT_AUTH_BROKER_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${key}` },
        body: JSON.stringify({ method: request.method,
          path: destination.pathname.slice('/auth/v1'.length),
          query: destination.search, body: request.method === 'GET' ? undefined : await request.text(), userToken }),
        signal: request.signal, redirect: 'error',
      });
    }
    if (['/auth/v1/signup', '/auth/v1/otp', '/auth/v1/recover', '/auth/v1/resend', '/auth/v1/invite', '/auth/v1/reauthenticate'].includes(destination.pathname)) {
      throw new Error('Development authentication messages must use the canonical email/SMS capture engine');
    }
    if (destination.pathname === '/auth/v1/user' && request.method === 'PUT') {
      const update = await request.clone().json();
      if (update.email || update.phone) throw new Error('Development identity changes must use the canonical email/SMS capture engine');
    }
    if (['/auth/v1/user', '/auth/v1/logout'].includes(destination.pathname) &&
      credentialRole((request.headers.get('authorization') ?? '').replace(/^Bearer /, '')) !== 'development_authenticated') {
      return new Response(JSON.stringify({ msg: 'Development session required', code: 'development_identity_required' }), {
        status: 403, headers: { 'Content-Type': 'application/json' },
      });
    }
    const response = await originalFetch(request);
    if (response.ok && ['/auth/v1/token', '/auth/v1/verify', '/auth/v1/user'].includes(destination.pathname)) {
      const payload = await response.clone().json() as {
        access_token?: string; user?: { app_metadata?: Record<string, unknown> }; app_metadata?: Record<string, unknown>;
      };
      const metadata = payload.user?.app_metadata ?? payload.app_metadata;
      if (metadata?.bizrnr_environment !== 'development' ||
        (payload.access_token && credentialRole(payload.access_token) !== 'development_authenticated')) {
        return new Response(JSON.stringify({ msg: 'Use a development account for this environment', code: 'development_identity_required' }), {
          status: 403, headers: { 'Content-Type': 'application/json' },
        });
      }
    }
    return response;
  };
  return {
    ...options,
    auth: { ...options?.auth, storageKey: `sb-${applicationProjectRef(url)}-development-auth-token` },
    realtime: { ...options?.realtime, params: { ...options?.realtime?.params, apikey: gatewayKey } },
    db: { ...options?.db, schema: 'development' },
    global: { ...options?.global, headers: { ...options?.global?.headers, apikey: gatewayKey }, fetch: scopedFetch },
  };
}
